import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCalendarPlus, FaFileImport } from 'react-icons/fa';
import { MdContentCopy } from 'react-icons/md';
import { isAndroid, isIOS, isMacOs, isWindows } from 'react-device-detect';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import Button from '../buttons/Button';

interface AddToCalendarModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function AddToCalendarModal({
  open,
  setOpen,
}: AddToCalendarModalProps): JSX.Element {
  const { t } = useTranslation();
  const business = useSelectedBusiness();

  const base64BusinessId = btoa(business?.businessId?.toString() || '');
  const subscribeUrl = `${process.env.REACT_APP_API_URL}/calendar/ics/${base64BusinessId}`;
  const webcalUrl = subscribeUrl.replace(/^https?:\/\//, 'webcal://');
  const downloadUrl = `${process.env.REACT_APP_API_URL}/calendar/ics/${base64BusinessId}/upcoming`;

  const redirectToUrl = (url: string, method: 'href' | 'open', target?: string, rel?: string) => {
    try {
      if (method === 'href') {
        window.location.href = url;
      } else if (method === 'open') {
        window.open(url, target || '_blank', rel || 'noopener,noreferrer');
      }
    } catch (error) {
      if (method === 'href') {
        window.open(url, target || '_blank', rel || 'noopener,noreferrer');
      }
    }
  };

  const handleDownloadCalendar = () => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `events-${business?.businessId || 'calendar'}.ics`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubscribeCalendar = () => {
    if (isIOS) {
      redirectToUrl(webcalUrl, 'href');
    } else if (isAndroid) {
      const googleCalUrl = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(subscribeUrl)}`;
      redirectToUrl(googleCalUrl, 'open');
    } else if (isMacOs || isWindows) {
      redirectToUrl(webcalUrl, 'href');
    } else {
      handleDownloadCalendar();
    }
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(subscribeUrl);
      toast.success(t('page.event.calendar.shareLinkCopied'));
    } catch (error) {
      toast.error(t('page.event.calendar.shareLinkCopyError'));
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="max-w-4xl w-full mx-4 rounded-xl bg-secondary-50 p-6">
      <div className="flex items-center gap-3 mb-6">
        <FaCalendarPlus className="h-6 w-6 text-primary-500" />
        <h2 className="text-2xl font-semibold text-primary-900">
          {t('page.event.calendar.add')}
        </h2>
      </div>
      <div className="max-h-[70vh] overflow-y-auto">
        <div className="w-full h-full flex flex-col gap-6">
          {/* One-time Import Section */}
          <div className="bg-secondary-200 bg-opacity-50 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <FaFileImport className="h-6 w-6 text-primary-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-primary-900 mb-2">
                  {t('page.event.calendar.oneTimeImport')}
                </h4>
                <p className="text-primary-700 mb-4">
                  {t('page.event.calendar.oneTimeImportDescription')}
                </p>
                <Button
                  variant="primary"
                  onClick={handleDownloadCalendar}
                  className="flex items-center gap-2">
                  <FaFileImport className="h-5 w-5" />
                  {t('page.event.calendar.add')}
                </Button>
              </div>
            </div>
          </div>

          {/* Subscribe Section */}
          <div className="bg-secondary-200 bg-opacity-50 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <FaCalendarPlus className="h-6 w-6 text-primary-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-primary-900 mb-2">
                  {t('page.event.calendar.subscribe')}
                </h4>
                <p className="text-primary-700 mb-4">
                  {t('page.event.calendar.subscribeDescription')}
                </p>
                <Button
                  variant="primary"
                  onClick={handleSubscribeCalendar}
                  className="flex items-center gap-2">
                  <FaCalendarPlus className="h-5 w-5" />
                  {t('page.event.calendar.subscribe')}
                </Button>
              </div>
            </div>
          </div>

          {/* Share with Community Section */}
          <div className="bg-secondary-50 rounded-lg">
            <h3 className="font-semibold text-xl text-primary-900 mb-4">
              {t('page.event.calendar.shareWithCommunity')}
            </h3>

            <div className="flex flex-col gap-3 pl-2">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-primary-900">
                  {t('page.event.calendar.publicCalendarUrl')}
                </label>
                <div className="flex items-center gap-2 w-1/2">
                  <input
                    type="text"
                    className="flex-1 rounded-full border border-primary bg-secondary-50 px-4 py-2 font-mono text-lg font-semibold"
                    value={subscribeUrl}
                    readOnly
                  />
                  <Button
                    variant="secondary"
                    onClick={handleCopyShareLink}
                    className="rounded-full p-3"
                  >
                    <MdContentCopy className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="text-sm text-primary-600">
                <p className="mb-2">
                  <strong>{t('page.event.calendar.howToUse')}:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>{t('page.event.calendar.shareStep1')}</li>
                  <li>{t('page.event.calendar.shareStep2')}</li>
                  <li>{t('page.event.calendar.shareStep3')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}